import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { EASE } from "../../lib/site";

const RING = "LASER DAY • AISLA STUDIO • ";

const SpinBadge = () => (
  <div className="absolute -right-2 -top-4 h-28 w-28 sm:h-32 sm:w-32" aria-hidden="true">
    <svg viewBox="0 0 100 100" className="h-full w-full animate-[spin_22s_linear_infinite] motion-reduce:animate-none">
      <defs>
        <path id="circ" d="M50 50 m-38 0 a38 38 0 1 1 76 0 a38 38 0 1 1 -76 0" />
      </defs>
      <circle cx="50" cy="50" r="49" fill="#2D1C18" />
      <text fill="#F3D9CE" fontSize="10" fontWeight="600" letterSpacing="3.1">
        <textPath href="#circ">{RING}</textPath>
      </text>
    </svg>
    <span className="absolute inset-0 grid place-items-center font-script text-3xl text-[#E9B3A0]">a</span>
  </div>
);

const PriceTag = () => (
  <motion.div
    initial={{ opacity: 0, x: -30, rotate: -6 }}
    animate={{ opacity: 1, x: 0, rotate: -4 }}
    transition={{ delay: 1.4, duration: 1, ease: EASE }}
    className="absolute -left-3 bottom-8 rounded-2xl border border-white/60 bg-[#FFFDF9]/85 px-5 py-4 shadow-[0_24px_60px_-20px_rgba(142,63,48,0.45)] backdrop-blur-md sm:-left-10"
    data-testid="hero-price-tag"
  >
    <p className="text-[0.7rem] font-bold tracking-[0.22em] text-[#9A4A38]">VIRILHA COMPLETA</p>
    <p className="mt-1 flex items-baseline gap-1 text-[#2D1C18]">
      <span className="text-xs font-semibold">por apenas R$</span>
      <span className="font-display text-4xl text-metal">49,90</span>
    </p>
  </motion.div>
);

export const HeroVisual = () => {
  const ref = useRef(null);
  const rx = useSpring(0, { stiffness: 110, damping: 16 });
  const ry = useSpring(0, { stiffness: 110, damping: 16 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <div ref={ref} className="relative mx-auto w-[min(84vw,440px)] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={onLeave}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <div className="absolute -inset-3 rounded-t-[999px] rounded-b-[2.4rem] border border-[#B8654F]/40" aria-hidden="true" />
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 32px 32px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 32px 32px)" }}
          transition={{ duration: 1.5, ease: EASE, delay: 0.3 }}
          className="relative aspect-[3/4] overflow-hidden rounded-t-[999px] rounded-b-[2rem] bg-[#EBD9CB] shadow-[0_40px_90px_-30px_rgba(45,28,24,0.45)]"
        >
          <motion.img
            style={{ y: imgY }}
            src="/img/hero.webp"
            alt="Mulher sorrindo com a pele lisa e o braço erguido, vestindo top branco, sobre tecido de cetim champanhe"
            className="h-full w-full scale-[1.18] object-cover object-[50%_30%]"
            fetchPriority="high"
            data-testid="hero-image"
          />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(255,240,230,0.35),transparent_60%)]" aria-hidden="true" />
        </motion.div>
        <SpinBadge />
        <PriceTag />
      </motion.div>
    </div>
  );
};
